/**
 * Medical Report Service
 * Handles all medical report export-related API calls and utilities
 */

import axios from "axios";
import { API_BASE_URL } from "../app_url";
import html2canvas from "html2canvas";

// Auth header helper
const getHeader = () => {
  let value = JSON.parse(localStorage.getItem("doctolikLoginInfo"));
  let header = {
    headers: {
      Authorization: "Bearer " + value?.authorisation?.token,
      'Cache-Control': 'no-cache',
    },
  };
  return header;
};

/**
 * Export field definitions for each data category
 * Used to configure which fields can be exported per appointment
 */
export const EXPORT_FIELDS = {
  general: {
    label: "General information",
    fields: [
      { key: "appointment_date", label: "Date", default: true },
      { key: "appointment_time", label: "Heure", default: true },
      { key: "appointment_type", label: "Type", default: true },
      { key: "status", label: "Statut", default: false },
      { key: "reason", label: "Reason for consultation", default: true },
    ]
  },
  observations: {
    label: "Observations",
    fields: [
      { key: "medical_condition_title", label: "Condition", default: true },
      { key: "examination", label: "Examination notes", default: true },
      { key: "other_notes", label: "Other notes", default: true },
      { key: "reminder_note", label: "Reminder notes", default: false },
    ]
  },
  prescriptions: {
    label: "Prescriptions",
    fields: [
      { key: "template_name", label: "Template name", default: true },
      { key: "prescription_body", label: "Prescription body", default: true },
      { key: "prescription_date", label: "Prescription date", default: true },
    ]
  },
  charts: {
    label: "Measurements & Charts",
    fields: [
      { key: "property", label: "Parameter", default: true },
      { key: "value", label: "Value", default: true },
      { key: "measured_at", label: "Measurement date", default: true },
    ]
  },
  careSteps: {
    label: "Care protocol",
    fields: [
      { key: "service", label: "Service name", default: true },
      { key: "is_done", label: "Status", default: true },
      { key: "comment", label: "Comment", default: false },
    ]
  },
  documents: {
    label: "Documents",
    fields: [
      { key: "name", label: "Document name", default: true },
      { key: "file_type", label: "File type", default: true },
      { key: "created_at", label: "Added date", default: true },
    ]
  },
  medicalBackground: {
    label: "Medical history",
    fields: [
      { key: "title", label: "Category", default: true },
      { key: "description", label: "Description", default: true },
    ]
  },
};

/**
 * Fetch patient medical report data
 * @param {string} patientId - Patient ID
 * @returns {Promise} API response with patient data
 */
export const fetchPatientMedicalReportApi = (patientId) => {
  const fd = new FormData();
  fd.append("id", patientId);
  return axios.post(API_BASE_URL + `doctor/getPatientDictionaryDetails`, fd, getHeader());
};

/**
 * Fetch patient family list
 * @param {string} patientId - Patient ID
 * @returns {Promise} API response with family members
 */
export const fetchPatientFamilyApi = (patientId) => {
  const fd = new FormData();
  fd.append("patient_id", patientId);
  return axios.post(API_BASE_URL + `doctor/patientFamilyList`, fd, getHeader());
};

/**
 * Fetch care steps for a specific appointment
 * @param {string} doctorId - Doctor ID
 * @param {string} patientId - Patient ID
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise} API response with care steps
 */
export const fetchAppointmentCareStepsApi = async (doctorId, patientId, appointmentId) => {
  const url = `${API_BASE_URL}doctors/${doctorId}/protocolsoins?patientId=${patientId}&appointmentId=${appointmentId}`;
  return axios.get(url, getHeader());
};

/**
 * Fetch chart data for a specific appointment
 * @param {string} doctorId - Doctor ID
 * @param {string} patientId - Patient ID
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise} API response with chart data
 */
export const fetchAppointmentChartsApi = async (doctorId, patientId, appointmentId) => {
  const url = `${API_BASE_URL}doctors/${doctorId}/patients/${patientId}/weights?appointmentId=${appointmentId}`;
  return axios.get(url, getHeader());
};

/**
 * Fetch documents for a specific appointment
 * @param {string} doctorId - Doctor ID
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise} API response with documents
 */
export const fetchAppointmentDocumentsApi = async (doctorId, appointmentId) => {
  const url = `${API_BASE_URL}doctors/${doctorId}/appointments/${appointmentId}/documents`;
  return axios.get(url, getHeader());
};

/**
 * Process appointment data for export
 * Filters and transforms data based on selected fields
 */
export const processAppointmentDataForExport = (appointment, selectedFields) => {
  const processed = {};

  // Process general info
  if (selectedFields.general) {
    processed.general = {};
    selectedFields.general.forEach(field => {
      if (appointment[field]) {
        processed.general[field] = appointment[field];
      }
    });
  }

  // Process observations
  if (selectedFields.observations && appointment.observations?.[0]) {
    processed.observations = {};
    const obs = appointment.observations[0];
    selectedFields.observations.forEach(field => {
      if (obs[field]) {
        processed.observations[field] = obs[field];
      }
    });
  }

  // Process prescriptions
  if (selectedFields.prescriptions && appointment.prescription_content?.length > 0) {
    processed.prescriptions = appointment.prescription_content.map(pres => {
      const filtered = {};
      selectedFields.prescriptions.forEach(field => {
        if (pres[field]) {
          filtered[field] = pres[field];
        }
      });
      return filtered;
    });
  }

  // Process care steps
  if (selectedFields.careSteps && appointment.careSteps?.length > 0) {
    processed.careSteps = appointment.careSteps.map(step => {
      const filtered = {};
      selectedFields.careSteps.forEach(field => {
        if (step[field] !== undefined) {
          filtered[field] = step[field];
        }
      });
      return filtered;
    });
  }

  // Process charts
  if (selectedFields.charts && appointment.charts?.length > 0) {
    processed.charts = appointment.charts.map(chart => {
      const filtered = {};
      selectedFields.charts.forEach(field => {
        if (chart[field] !== undefined) {
          filtered[field] = chart[field];
        }
      });
      return filtered;
    });
  }

  // Process documents
  if (selectedFields.documents && appointment.documents?.length > 0) {
    processed.documents = appointment.documents.map(doc => {
      const filtered = {};
      selectedFields.documents.forEach(field => {
        if (doc[field]) {
          filtered[field] = doc[field];
        }
      });
      return filtered;
    });
  }

  return processed;
};

/**
 * Generate PDF from HTML element
 * Uses browser print dialog to save as PDF
 * @param {HTMLElement} element - HTML element to convert
 * @param {string} filename - Output filename (used for naming suggestion)
 */
export const generatePDFFromElement = async (element, filename = "medical-report.pdf") => {
  try {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      throw new Error('Unable to open print window. Please allow popups for this site.');
    }

    // Clone the element and its styles
    const clonedElement = element.cloneNode(true);
    
    // Get all stylesheets
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          // Handle cross-origin stylesheets
          if (styleSheet.href) {
            return `@import url("${styleSheet.href}");`;
          }
          return '';
        }
      })
      .join('\n');

    // Build the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename.replace('.pdf', '')}</title>
          <style>
            ${styles}
            @media print {
              body {
                margin: 0;
                padding: 20px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .report-preview {
                box-shadow: none !important;
              }
              .report-cover-page {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .cover-page-break {
                break-after: page !important;
                page-break-after: always !important;
                height: 0;
                margin: 0;
                padding: 0;
              }
              .report-preview__header,
              .report-preview__patient,
              .report-preview__title,
              .patient-card {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .appointment-section {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .section-block {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .prescription-item,
              .care-step,
              .chart-item,
              .document-item {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
              }
              .document-pdf-item {
                break-inside: auto !important;
                page-break-inside: auto !important;
              }
              .document-pdf-embed {
                padding: 0 !important;
              }
              .pdf-page-canvas {
                break-inside: avoid !important;
                page-break-inside: avoid !important;
                width: 100% !important;
                height: auto !important;
              }
              .pdf-page-renderer__loading {
                display: none !important;
              }
              .appointment-section__header,
              .section-block h4 {
                break-after: avoid !important;
                page-break-after: avoid !important;
              }
            }
          </style>
        </head>
        <body>
          ${clonedElement.outerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load
    await new Promise(resolve => {
      printWindow.onload = resolve;
      setTimeout(resolve, 500); // Fallback
    });

    // Trigger print dialog
    printWindow.print();

    // Close window after a delay (user may cancel)
    setTimeout(() => {
      printWindow.close();
    }, 1000);

    return true;
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};

/**
 * Generate JSON export file
 * @param {Object} data - Data to export
 * @param {string} filename - Output filename
 */
export const generateJSONExport = (data, filename = "medical-report.json") => {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Format date for display
 * @param {string} date - Date string
 * @returns {string} Formatted date
 */
export const formatReportDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
};

/**
 * Get default selected fields (those marked as default: true)
 */
export const getDefaultSelectedFields = () => {
  const defaults = {};
  Object.keys(EXPORT_FIELDS).forEach(category => {
    defaults[category] = EXPORT_FIELDS[category].fields
      .filter(field => field.default)
      .map(field => field.key);
  });
  return defaults;
};

export default {
  EXPORT_FIELDS,
  fetchPatientMedicalReportApi,
  fetchPatientFamilyApi,
  fetchAppointmentCareStepsApi,
  fetchAppointmentChartsApi,
  fetchAppointmentDocumentsApi,
  processAppointmentDataForExport,
  generatePDFFromElement,
  generateJSONExport,
  formatReportDate,
  getDefaultSelectedFields,
};
